/* eslint-disable no-redeclare */
import {
  ApplicationInvoice,
  ApplicationSubscription,
  createMandateLink as createMandateLinkMutation,
  CreateMandateLinkMutation,
  CreateMandateLinkMutationVariables,
  CreditCost,
  CreditUsageCost,
  DateTime,
  disableClientAccount as deleteMutation,
  DisableClientAccountMutationVariables,
  DisableClientAccountResponse,
  getCreditCostsForClient as getCreditCostsForClientQuery,
  getDateTimeDifference,
  syncApplicationInvoices as syncApplicationInvoicesQuery,
  syncCreditUsageCosts as syncCreditUsageCostsQuery,
  syncApplicationSubscriptions as syncQuery,
  todayDateTime,
} from '@rentguru/commons-utils';
import { mutation, NUMBER_OF_MINUTES_FOR_REFETCH } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useUser } from './UserContext';
import { adminToolGraphql } from './utils/admintoolUtils';

export interface ClientContext extends ClientState {
  setFetchClient: () => void;
  initiateMandateSetup: () => Promise<string | undefined>;
  getCreditCostsForClient: () => Promise<CreditCost[]>;
  disableClient: (id: string, reauthToken: string) => Promise<boolean>;
}

interface ClientState {
  applicationSubscription: ApplicationSubscription | null;
  applicationInvoices: ApplicationInvoice[] | null;
  creditUsageCosts: CreditUsageCost[] | null;
  error?: string;
  loading: boolean;
  shouldFetch: boolean;
  lastFetch: DateTime | null;
}

type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
    }
  | {
      type: 'FETCHED';
      payload: {
        applicationSubscription: ApplicationSubscription | null;
        applicationInvoices: ApplicationInvoice[];
        creditUsageCosts: CreditUsageCost[];
      };
    }
  | {
      type: 'FETCH_ERROR';
      payload: {
        error: string;
      };
    };

const initialState: ClientState = {
  loading: false,
  error: undefined,
  applicationSubscription: null,
  applicationInvoices: null,
  creditUsageCosts: null,
  shouldFetch: false,
  lastFetch: null,
};

const clientReducer = (state: ClientState, action: Action): ClientState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (
        state.loading ||
        state.shouldFetch ||
        (state.lastFetch &&
          getDateTimeDifference({ start: state.lastFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
      };
    case 'IS_FETCHING':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHED':
      // Build List with merged object
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        applicationSubscription: action.payload.applicationSubscription,
        applicationInvoices: action.payload.applicationInvoices,
        creditUsageCosts: action.payload.creditUsageCosts,
        lastFetch: todayDateTime(),
      };
    case 'FETCH_ERROR':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        error: action.payload.error,
      };

    default:
      return state;
  }
};

const fetchClient = async (clientId: string): Promise<ApplicationSubscription[]> =>
  await adminToolGraphql.list<ApplicationSubscription>(syncQuery, 'clientId', clientId);

const fetchApplicationInvoices = async (clientId: string): Promise<ApplicationInvoice[]> =>
  await adminToolGraphql.list<ApplicationInvoice>(syncApplicationInvoicesQuery, 'clientId', clientId);

const fetchCreditUsageCosts = async (): Promise<CreditUsageCost[]> =>
  await adminToolGraphql.list<CreditUsageCost>(syncCreditUsageCostsQuery);

export const ClientContext = React.createContext<ClientContext | null>(null);

export const ClientContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<ClientState, Action>>(clientReducer, initialState);
  const { clientId } = useUser();

  const setFetchClient = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  useEffect(() => {
    if (state.shouldFetch) fetchAndSetClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  const fetchAndSetClient = async () => {
    try {
      dispatch({ type: 'IS_FETCHING' });
      const applicationSubscriptions = await fetchClient(clientId!);
      const applicationInvoices = await fetchApplicationInvoices(clientId!);
      const creditUsageCosts = await fetchCreditUsageCosts();
      dispatch({
        type: 'FETCHED',
        payload: {
          applicationSubscription: applicationSubscriptions?.[0] ?? null,
          applicationInvoices,
          creditUsageCosts,
        },
      });
    } catch (error) {
      console.error('[client] fetching the client subscription failed', error);
      dispatch({ type: 'FETCH_ERROR', payload: { error: (error as Error)?.message ?? String(error) } });
    }
  };

  const initiateMandateSetup = async (): Promise<string | undefined> => {
    const result = await mutation<
      NonNullable<CreateMandateLinkMutation['createMandateLink']>,
      CreateMandateLinkMutationVariables
    >(createMandateLinkMutation, {
      input: {
        clientId: clientId!,
      },
    });
    return result?.id;
  };

  const getCreditCostsForClient = async (): Promise<CreditCost[]> =>
    await adminToolGraphql.list<CreditCost>(getCreditCostsForClientQuery, undefined, undefined, {
      clientId: clientId!,
    });

  const disableClient = async (id: string, reauthToken: string): Promise<boolean> => {
    try {
      const response = await mutation<DisableClientAccountResponse, DisableClientAccountMutationVariables>(
        deleteMutation,
        { input: { clientId: id, reauthToken } }
      );
      if (response) {
        dispatch({ type: 'SHOULD_FETCH' });
      }
      return !isNil(response);
    } catch (error) {
      dispatch({ type: 'FETCH_ERROR', payload: { error: (error as Error)?.message ?? String(error) } });
      return false;
    }
  };

  const values = useMemo(
    () => ({
      ...state,
      setFetchClient,
      fetchAndSetClient,
      initiateMandateSetup,
      getCreditCostsForClient,
      disableClient,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <ClientContext.Provider value={values}>{children}</ClientContext.Provider>;
};

export const useClient = (): ClientContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<ClientContext | null>(ClientContext);

  if (isNil(context)) {
    throw new Error('`useClient` hook must be used within a `ClientContext` component');
  }
  useEffect(() => {
    if (!firstRender.current && isNil(context.applicationSubscription)) {
      context.setFetchClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context;
};
